import React, { useState, useEffect, useMemo, useCallback } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import SafeImage from '../SafeImage';

const COLORS_DEFAULT = {
  bg: '#F8F7F4',
  card: '#FFFFFF',
  text: '#1F2937',
  textSecondary: '#6B7280',
  accent: '#D4A64A',
  border: '#E5E7EB',
  success: '#22C55E',
  danger: '#EF4444',
  veg: '#16A34A',
  nonVeg: '#DC2626'
};

const formatPrice = (price) => `₹${Number(price || 0).toFixed(0)}`;

const getInitials = (name = '') => {
  return name
    .split(' ')
    .map((n) => n[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
};

const resolveMediaUrl = (url, suffix = '') => {
  if (!url) return '';
  if (typeof url !== 'string') return '';

  let raw = url.trim();

  // Prepend https:// if it looks like an absolute web URL without a protocol
  if (!/^https?:\/\//i.test(raw)) {
    const firstSlashIdx = raw.indexOf('/');
    const hostPart = firstSlashIdx === -1 ? raw : raw.substring(0, firstSlashIdx);
    const isImageExtension = /\.(png|jpg|jpeg|gif|webp|svg)$/i.test(hostPart);
    if (hostPart.includes('.') && 
        !hostPart.startsWith('/') && 
        hostPart !== 'localhost' && 
        !isImageExtension) {
      raw = `https://${raw}`;
    }
  }

  // Convert Google Drive view URLs to direct stream URLs
  if (raw.includes('drive.google.com')) {
    let fileId = '';
    const fileDMatch = raw.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (fileDMatch && fileDMatch[1]) {
      fileId = fileDMatch[1];
    } else {
      const idParamMatch = raw.match(/[?&]id=([a-zA-Z0-9_-]+)/);
      if (idParamMatch && idParamMatch[1]) {
        fileId = idParamMatch[1];
      }
    }
    if (fileId) {
      raw = `https://lh3.googleusercontent.com/d/${fileId}`;
    }
  }

  // Convert Imgur page links to direct image links
  if (raw.includes('imgur.com') && !raw.includes('i.imgur.com')) {
    if (!/\/a\//i.test(raw) && !/\/gallery\//i.test(raw)) {
      const match = raw.match(/imgur\.com\/([a-zA-Z0-9]+)/);
      if (match && match[1]) {
        raw = `https://i.imgur.com/${match[1]}.png`;
      }
    }
  }

  // Apply Google User Content suffix if requested
  if (raw.includes('googleusercontent.com') && suffix) {
    if (!/=[swh0-9-]+$/i.test(raw)) {
      raw = `${raw}${suffix}`;
    }
  }

  if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('data:')) return raw;
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:8081').replace(/\/+$/, '');
  const path = raw.startsWith('/') ? raw : `/${raw}`;
  return `${baseUrl}${path}`;
};

const isExternalUrl = (url) => {
  if (!url) return false;
  const raw = String(url).trim();
  return /^https?:\/\//i.test(raw) && !raw.includes('localhost') && !raw.includes('127.0.0.1');
};

const ProductCarousel = ({
  section,
  cart,
  onAdd,
  onUpdateQuantity,
  onOpenDescription,
  restaurantOpen,
  getProductDisplayPriceMeta,
  getProductQuantity,
  LazyImage,
  COLORS = COLORS_DEFAULT
}) => {
  const items = section.items || [];
  const total = items.length;
  const gap = 12;

  const [containerWidth, setContainerWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 0);

  const slideMetrics = useMemo(() => {
    const cw = containerWidth || (typeof window !== 'undefined' ? window.innerWidth : 360);
    let width;
    if (cw < 480) width = cw * 0.62;
    else if (cw < 768) width = cw * 0.42;
    else if (cw < 1024) width = cw * 0.30;
    else width = 260;
    width = Math.max(200, Math.min(320, width));
    return { width: Math.round(width), gap };
  }, [containerWidth]);

  const itemsFit = useMemo(() => {
    const { width, gap } = slideMetrics;
    const totalWidth = total * width + (total - 1) * gap;
    const cw = containerWidth || (typeof window !== 'undefined' ? window.innerWidth : 360);
    return totalWidth <= cw;
  }, [total, slideMetrics, containerWidth]);

  const [emblaRef, emblaApi] = useEmblaCarousel(
    total > 1
      ? { loop: !itemsFit, align: 'center', skipSnaps: false, dragFree: false }
      : { align: 'center', watchDrag: false },
    []
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const updateSlideStyles = useCallback(() => {
    if (!emblaApi) return;
    const container = emblaApi.containerNode();
    const viewport = emblaApi.rootNode();
    if (!container || !viewport) return;

    const slides = container.querySelectorAll('.pc-embla-slide');
    if (!slides.length) return;

    if (itemsFit) {
      slides.forEach((slide) => {
        slide.style.transform = 'scale(1)';
        slide.style.opacity = '1';
        slide.style.zIndex = '1';
        slide.classList.add('is-center');
      });
      return;
    }

    const viewportRect = viewport.getBoundingClientRect();
    const center = viewportRect.left + viewportRect.width / 2;

    const slideWidth = slides[0].offsetWidth;
    const marginRight = parseFloat(window.getComputedStyle(slides[0]).marginRight) || 0;
    const step = slideWidth + marginRight;
    if (step < 10) return;

    let minDiff = Infinity;
    let centerSlide = null;

    slides.forEach((slide) => {
      const rect = slide.getBoundingClientRect();
      const slideCenter = rect.left + rect.width / 2;
      const diff = Math.abs(slideCenter - center);

      if (diff < minDiff) {
        minDiff = diff;
        centerSlide = slide;
      }

      let scale = 0.82;
      let opacity = 0.75;

      if (diff <= step) {
        const t = diff / step;
        scale = 1 - t * 0.1;
        opacity = 1 - t * 0.1;
      } else if (diff <= 2 * step) {
        const t = (diff - step) / step;
        scale = 0.9 - t * 0.08;
        opacity = 0.9 - t * 0.15;
      }

      const cardDist = diff / step;
      const zIndex = Math.max(1, Math.round(slides.length - cardDist));

      slide.style.transform = `scale(${scale})`;
      slide.style.opacity = opacity;
      slide.style.zIndex = zIndex;
    });

    slides.forEach((slide) => {
      if (slide === centerSlide) slide.classList.add('is-center');
      else slide.classList.remove('is-center');
    });
  }, [emblaApi, itemsFit]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };
    const onScroll = () => updateSlideStyles();

    emblaApi.on('select', onSelect);
    emblaApi.on('scroll', onScroll);
    emblaApi.on('reInit', onScroll);

    onSelect();
    requestAnimationFrame(onScroll);

    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('scroll', onScroll);
      emblaApi.off('reInit', onScroll);
    };
  }, [emblaApi, updateSlideStyles]);

  useEffect(() => {
    if (emblaApi) {
      emblaApi.reInit({
        loop: !itemsFit && total > 1,
        watchDrag: true,
        align: 'center'
      });
    }
  }, [emblaApi, itemsFit, total]);

  useEffect(() => {
    const measure = () => {
      const root = emblaApi?.rootNode();
      if (root) setContainerWidth(root.offsetWidth);
    };
    measure();
    const onResize = () => {
      measure();
      requestAnimationFrame(updateSlideStyles);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [emblaApi, updateSlideStyles]);

  useEffect(() => {
    if (emblaApi) {
      setSelectedIndex(0);
      emblaApi.scrollTo(0, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emblaApi, section.sectionId, section.name, items.length]);

  const handleDotClick = useCallback((idx) => {
    if (emblaApi) emblaApi.scrollTo(idx);
  }, [emblaApi]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => {
      setCanScrollPrev(emblaApi.canScrollPrev());
      setCanScrollNext(emblaApi.canScrollNext());
    };
    emblaApi.on('select', onSelect);
    emblaApi.on('reInit', onSelect);
    onSelect();
    return () => {
      emblaApi.off('select', onSelect);
      emblaApi.off('reInit', onSelect);
    };
  }, [emblaApi]);

  if (total === 0) return null;

  return (
    <div style={{ position: 'relative' }} className="pc-carousel-wrapper">
      {total > 1 && !itemsFit && canScrollPrev && (
        <button
          type="button"
          className="pc-arrow-btn"
          style={{ left: '-20px' }}
          onClick={scrollPrev}
          aria-label="Previous slide"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}
      {total > 1 && !itemsFit && canScrollNext && (
        <button
          type="button"
          className="pc-arrow-btn"
          style={{ right: '-20px' }}
          onClick={scrollNext}
          aria-label="Next slide"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      )}
      <div
        className="pc-embla-viewport"
        ref={emblaRef}
        style={{
          overflow: 'hidden',
          padding: '8px 0 18px',
          cursor: 'grab'
        }}
      >
        <div
          className="pc-embla-container"
          style={{
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: itemsFit ? 'center' : 'flex-start'
          }}
        >
          {items.map((product, idx) => {
            const qty = getProductQuantity(product.productId);
            const cartItem = cart.find((item) => item.productId === product.productId);
            const cartItemId = cartItem ? cartItem.cartItemId : null;

            return (
              <div
                key={product.productId}
                className="pc-carousel-item pc-embla-slide"
                style={{
                  flex: '0 0 auto',
                  width: `${slideMetrics.width}px`,
                  marginRight: `${slideMetrics.gap}px`,
                  willChange: 'transform, opacity',
                  userSelect: 'none'
                }}
              >
                <ProductCard
                  product={product}
                  qty={qty}
                  onAdd={onAdd}
                  onUpdateQuantity={onUpdateQuantity}
                  onOpenDescription={onOpenDescription}
                  restaurantOpen={restaurantOpen}
                  getProductDisplayPriceMeta={getProductDisplayPriceMeta}
                  cartItemId={cartItemId}
                  LazyImage={LazyImage}
                  COLORS={COLORS}
                />
              </div>
            );
          })}
        </div>
      </div>

      {total > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: '5px', marginTop: '-4px', marginBottom: '12px' }}>
          {items.map((_, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => handleDotClick(idx)}
              style={{
                width: idx === selectedIndex ? '18px' : '5px',
                height: '5px',
                borderRadius: '999px',
                border: 'none',
                background: idx === selectedIndex ? COLORS.accent : 'rgba(0,0,0,0.12)',
                transition: 'all 0.25s ease',
                cursor: 'pointer',
                padding: 0
              }}
              aria-label={`Go to item ${idx + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ProductCard = React.memo(({
  product,
  qty,
  onAdd,
  onUpdateQuantity,
  onOpenDescription,
  restaurantOpen,
  cartItemId,
  getProductDisplayPriceMeta,
  LazyImage,
  COLORS = COLORS_DEFAULT
}) => {
  const priceMeta = useMemo(() => {
    if (typeof getProductDisplayPriceMeta === 'function') {
      return getProductDisplayPriceMeta(product);
    }
    const base = Number(product?.discountPrice || product?.price || 0);
    return { price: base, estimatedFromModifiers: false };
  }, [product, getProductDisplayPriceMeta]);

  const showOriginalPrice = Number(product?.discountPrice) > 0 && Number(product?.price) > Number(product?.discountPrice);
  const hasDescription = Boolean(product.description);

  return (
    <div
      className="pc-product-card"
      style={{
        background: '#FFFFFF',
        borderRadius: '20px',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.04)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        padding: '8px',
        transition: 'box-shadow 0.3s ease, border-color 0.3s ease'
      }}
    >
      {/* Image Container */}
      <div className="pc-product-img-wrap" style={{
        position: 'relative',
        paddingTop: '100%',
        background: '#F9FAFB',
        borderRadius: '14px',
        overflow: 'hidden',
        marginBottom: '10px'
      }}>
        {product.imageUrl ? (
          LazyImage ? (
            <LazyImage
              src={resolveMediaUrl(product.imageUrl, '=s600')}
              alt={product.productName}
              index={0}
              className="pc-card-img"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              placeholderStyle={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                background: '#f9fafb'
              }}
              skeleton
            />
          ) : (
            <SafeImage
              src={resolveMediaUrl(product.imageUrl, '=s600')}
              alt={product.productName}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
              loading="lazy"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )
        ) : (
          <div style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: COLORS.textSecondary,
            fontSize: '16px',
            fontWeight: 700
          }}>
            {getInitials(product.productName)}
          </div>
        )}

        {/* Favorite icon */}
        <button
          type="button"
          className="pc-fav-btn"
          style={{
            position: 'absolute',
            top: '8px',
            right: '8px',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            border: 'none',
            background: 'rgba(255,255,255,0.95)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            zIndex: 2
          }}
          aria-label="Favorite"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#111827" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
          </svg>
        </button>

        {/* Badges */}
        {(product.isBestSeller || product.isRecommended) && (
          <div style={{ position: 'absolute', top: '8px', left: '8px', display: 'flex', flexDirection: 'column', gap: '3px', zIndex: 2 }}>
            {product.isBestSeller && (
              <span style={{
                background: COLORS.accent,
                color: '#fff',
                fontSize: '8px',
                fontWeight: 800,
                padding: '2px 6px',
                borderRadius: '999px',
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}>
                Bestseller
              </span>
            )}
            {product.isRecommended && (
              <span style={{
                background: '#111827',
                color: '#fff',
                fontSize: '8px',
                fontWeight: 800,
                padding: '2px 6px',
                borderRadius: '999px',
                textTransform: 'uppercase',
                letterSpacing: '0.03em'
              }}>
                Recommended
              </span>
            )}
          </div>
        )}
      </div>

      {/* Details Area */}
      <div style={{ padding: '0 2px 2px', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <h3 className="pc-card-title" style={{
          fontWeight: 800,
          color: '#111827',
          margin: '0 0 4px 0',
          lineHeight: '1.25'
        }}>
          {product.productName}
        </h3>

        {hasDescription && (
          <p
            onClick={() => onOpenDescription(product)}
            style={{
              fontSize: '11px',
              color: '#6B7280',
              margin: '0 0 8px 0',
              lineHeight: '1.4',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              cursor: 'pointer'
            }}
          >
            {product.description}
          </p>
        )}

        <div style={{ marginTop: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '15px', fontWeight: 800, color: '#111827' }}>
              {priceMeta.estimatedFromModifiers ? 'From ' : ''}{formatPrice(priceMeta.price)}
            </span>
            {showOriginalPrice && !priceMeta.estimatedFromModifiers && (
              <span style={{ fontSize: '10px', color: '#9CA3AF', textDecoration: 'line-through' }}>
                {formatPrice(product.price)}
              </span>
            )}
          </div>

          {qty > 0 ? (
            <div className="pc-qty-stepper" style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              background: COLORS.accent,
              borderRadius: '999px',
              padding: '2px',
              minWidth: '76px',
              justifyContent: 'space-between'
            }}>
              <button
                type="button"
                onClick={() => {
                  if (cartItemId) onUpdateQuantity(cartItemId, -1);
                }}
                style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  fontSize: '13px',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                −
              </button>
              <span style={{ fontSize: '12px', fontWeight: 700, color: '#fff', minWidth: '14px', textAlign: 'center' }}>{qty}</span>
              <button
                type="button"
                onClick={() => {
                  if (cartItemId) onUpdateQuantity(cartItemId, 1);
                }}
                style={{
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  border: 'none',
                  background: 'rgba(255,255,255,0.2)',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                +
              </button>
            </div>
          ) : (
            <button
              type="button"
              className="pc-add-pill"
              disabled={!restaurantOpen}
              onClick={() => onAdd(product)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                padding: '8px 14px',
                borderRadius: '999px',
                border: 'none',
                background: restaurantOpen ? COLORS.accent : '#D1D5DB',
                color: '#fff',
                fontSize: '12px',
                fontWeight: 700,
                cursor: restaurantOpen ? 'pointer' : 'not-allowed',
                boxShadow: restaurantOpen ? '0 4px 12px rgba(212, 166, 74, 0.25)' : 'none'
              }}
            >
              Add Item
            </button>
          )}
        </div>
      </div>
    </div>
  );
});

const PremiumCarouselTheme = (props) => {
  const {
    restaurant,
    isShopMode,
    restaurantOpen,
    closedMessage,
    categories,
    products,
    groupedMenuSections,
    cart,
    currentView,
    setCurrentView,
    searchQuery,
    setSearchQuery,
    fulfillmentType,
    selectFulfillmentType,
    addToCart,
    updateQuantity,
    removeFromCart,
    getCartCount,
    getCartTotal,
    getCartPricing,
    getProductDisplayPriceMeta,
    getProductQuantity,
    openCheckoutModal,
    openCartItemAddonModal,
    setEditingSpecialRequest,
    setShowClearCartConfirm,
    setDescriptionPopupProduct,
    showCategoryMenuModal,
    setShowCategoryMenuModal,
    toggleCategoryExpanded,
    expandedCategories,
    stickyFooterDeliveryInsight,
    appliedCoupon,
    handleRemoveCoupon,
    logoLoadError,
    setLogoLoadError,
    LazyImage,
    isMobile,
    currentTheme,
    getCartSubtotal,
    gstSettings,
    deliverySlabInsight,
    couponCode,
    setCouponCode,
    applyingCoupon,
    handleApplyCoupon,
    showCouponList,
    setShowCouponList,
    availableCoupons,
    couponsLoading,
    couponsError,
    handleToggleCoupons,
    formatCouponDescription,
    formatCouponExpiry,
    showSplash
  } = props;

  const COLORS = useMemo(() => ({
    bg: currentTheme?.bg || currentTheme?.gradient || COLORS_DEFAULT.bg,
    card: currentTheme?.glassCard || COLORS_DEFAULT.card,
    text: currentTheme?.primaryColor || COLORS_DEFAULT.text,
    textSecondary: currentTheme?.secondaryColor || COLORS_DEFAULT.textSecondary,
    accent: currentTheme?.accentColor || COLORS_DEFAULT.accent,
    border: currentTheme?.glassCardBorder || COLORS_DEFAULT.border,
    success: COLORS_DEFAULT.success,
    danger: COLORS_DEFAULT.danger,
    veg: COLORS_DEFAULT.veg,
    nonVeg: COLORS_DEFAULT.nonVeg
  }), [currentTheme]);

  const [filterOpen, setFilterOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    veg: false,
    nonVeg: false,
    bestSeller: false,
    recommended: false
  });
  const [viewAllCategory, setViewAllCategory] = useState(null);

  const cuisineType = restaurant?.cuisineType || (isShopMode ? 'Daily Essentials' : 'Multi Cuisine');

  const filteredSections = useMemo(() => {
    let sections = groupedMenuSections || [];
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      sections = sections
        .map((sec) => ({
          ...sec,
          items: sec.items.filter((p) =>
            (p.productName || '').toLowerCase().includes(q) ||
            (p.description || '').toLowerCase().includes(q)
          )
        }))
        .filter((sec) => sec.items.length > 0);
    }
    if (activeFilters.veg || activeFilters.nonVeg || activeFilters.bestSeller || activeFilters.recommended) {
      sections = sections
        .map((sec) => ({
          ...sec,
          items: sec.items.filter((p) => {
            if (activeFilters.veg && p.isVeg !== true) return false;
            if (activeFilters.nonVeg && p.isVeg !== false) return false;
            if (activeFilters.bestSeller && !p.isBestSeller) return false;
            if (activeFilters.recommended && !p.isRecommended) return false;
            return true;
          })
        }))
        .filter((sec) => sec.items.length > 0);
    }
    return sections;
  }, [groupedMenuSections, searchQuery, activeFilters]);

  const toggleFilter = (key) => {
    setActiveFilters((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const cartCount = getCartCount ? getCartCount() : cart.reduce((s, i) => s + i.quantity, 0);
  const cartTotal = getCartTotal ? getCartTotal() : 0;
  const cartPricing = typeof getCartPricing === 'function' ? getCartPricing() : null;

  const onAdd = useCallback((product) => {
    if (!restaurantOpen) return;
    addToCart(product);
  }, [restaurantOpen, addToCart]);

  const onOpenDescription = useCallback((product) => {
    if (setDescriptionPopupProduct) {
      setDescriptionPopupProduct(product);
    }
  }, [setDescriptionPopupProduct]);

  const scrollToCategory = (name) => {
    const el = document.getElementById(`pc-category-${name}`);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setShowCategoryMenuModal(false);
  };

      const renderHome = () => (
    <div className="pc-home" style={{ padding: '24px 18px calc(76px + env(safe-area-inset-bottom, 0px))', maxWidth: '720px', margin: '0 auto' }}>
      {/* Header */}
      <div className="pc-fade-up" style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '28px' }}>
        <div className="pc-logo" style={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: '#FFFFFF',
          boxShadow: '0 8px 24px rgba(0,0,0,0.06)',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          {restaurant?.logoUrl && !logoLoadError ? (
            <SafeImage 
              src={resolveMediaUrl(restaurant.logoUrl, '=s400')} 
              alt={restaurant.restaurantName} 
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
              fallback={<span style={{ fontSize: '20px', fontWeight: 800, color: COLORS.accent }}>{getInitials(restaurant?.restaurantName)}</span>}
              onError={(e) => {
                console.error('[DEBUG_LOG] (PremiumTheme) SafeImage failed to load logo. Attempted URL:', resolveMediaUrl(restaurant.logoUrl, '=s400'), e);
                setLogoLoadError(true);
              }} 
              crossOrigin={isExternalUrl(restaurant.logoUrl) ? "anonymous" : undefined}
            />
          ) : (
            <span style={{ fontSize: '20px', fontWeight: 800, color: COLORS.accent }}>{getInitials(restaurant?.restaurantName)}</span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ fontSize: '24px', fontWeight: 800, color: '#111827', margin: 0, lineHeight: 1.15, letterSpacing: '-0.4px' }}>{restaurant?.restaurantName}</h1>
          <p style={{ fontSize: '12px', color: '#D4A64A', margin: '5px 0 0 0', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.2px' }}>{cuisineType}</p>
        </div>
      </div>

      {/* Closed banner */}
      {!restaurantOpen && (
        <div className="pc-fade-up" style={{
          background: 'linear-gradient(135deg, #EF4444, #DC2626)',
          color: '#fff',
          borderRadius: '20px',
          padding: '16px 18px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          boxShadow: '0 8px 20px rgba(220, 38, 38, 0.15)'
        }}>
          <span style={{ fontSize: '24px' }}>🔒</span>
          <div>
            <div style={{ fontSize: '15px', fontWeight: 800 }}>Currently Closed</div>
            <div style={{ fontSize: '12px', opacity: 0.9, marginTop: '2px', lineHeight: 1.4 }}>{closedMessage || 'We are not taking orders right now.'}</div>
          </div>
        </div>
      )}

      {/* Hero Card */}
      <div
        className="pc-hero-card pc-fade-up"
        onClick={() => setCurrentView('menu')}
        style={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: '32px',
          padding: '34px 28px',
          marginBottom: '24px',
          background: 'linear-gradient(135deg, #FFFFFF 0%, #FFF9F0 55%, #FFF3E0 100%)',
          border: '1px solid rgba(212,166,74,0.12)',
          boxShadow: '0 20px 50px rgba(0,0,0,0.06)',
          cursor: 'pointer'
        }}
      >
        <div style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 18% 28%, rgba(212,166,74,0.09) 0%, transparent 28%), radial-gradient(circle at 82% 72%, rgba(212,166,74,0.07) 0%, transparent 28%)',
          pointerEvents: 'none'
        }} />
        <div style={{
          position: 'absolute',
          top: '-50px',
          right: '-50px',
          width: '180px',
          height: '180px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(212,166,74,0.12) 0%, rgba(212,166,74,0) 70%)',
          pointerEvents: 'none'
        }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div className="pc-float" style={{
            width: '64px',
            height: '64px',
            borderRadius: '20px',
            background: 'rgba(212,166,74,0.12)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginBottom: '22px'
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={COLORS.accent} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 13a4 4 0 0 1 0-8 4 4 0 0 1 7-2 4 4 0 0 1 7 2 4 4 0 0 1 0 8H6z"></path>
              <path d="M6 13v6a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2v-6"></path>
            </svg>
          </div>
          <h2 style={{ fontSize: '28px', fontWeight: 800, color: '#111827', margin: '0 0 10px 0', lineHeight: 1.15 }}>
            {isShopMode ? 'Explore Products' : 'Explore Our Menu'}
          </h2>
          <p style={{ fontSize: '15px', color: '#6B7280', margin: '0 0 30px 0', lineHeight: 1.5, maxWidth: '280px' }}>
            {isShopMode ? 'Best quality essentials at the best prices.' : 'Discover dishes crafted with passion and the finest ingredients.'}
          </p>
          <span className="pc-cta" style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: COLORS.accent,
            color: '#fff',
            fontSize: '14px',
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.6px',
            padding: '14px 26px',
            borderRadius: '999px',
            boxShadow: '0 8px 22px rgba(212,166,74,0.32)'
          }}>
            {isShopMode ? 'Browse Products' : 'Browse Menu'}
            <svg className="pc-cta-arrow" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14"></path>
              <path d="M12 5l7 7-7 7"></path>
            </svg>
          </span>
        </div>
      </div>

      {/* Social Bento Grid */}
      <div className="pc-fade-up" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '24px' }}>
        {restaurant?.contactPhone && (
          <a href={`tel:${restaurant.contactPhone}`} className="pc-social-card" style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#FFFFFF', borderRadius: '20px', padding: '16px', textDecoration: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.03)', overflow: 'hidden' }}>
            <div className="pc-social-icon" style={{ width: '44px', height: '44px', borderRadius: '14px', background: '#F0FDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#22C55E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
            </div>
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>Call</div>
              <div style={{ fontSize: '11px', color: '#6B7280', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{restaurant.contactPhone}</div>
            </div>
          </a>
        )}
        {restaurant?.instagramUrl && (
          <a href={restaurant.instagramUrl} target="_blank" rel="noopener noreferrer" className="pc-social-card" style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#FFFFFF', borderRadius: '20px', padding: '16px', textDecoration: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.03)', overflow: 'hidden' }}>
            <div className="pc-social-icon" style={{ width: '44px', height: '44px', borderRadius: '14px', background: '#FDF2F8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <defs>
                  <linearGradient id="pcInsta2" x1="0%" y1="100%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#f09433"/>
                    <stop offset="50%" stopColor="#dc2743"/>
                    <stop offset="100%" stopColor="#bc1888"/>
                  </linearGradient>
                </defs>
                <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#pcInsta2)"/>
                <rect x="6.5" y="6.5" width="11" height="11" rx="3.5" fill="none" stroke="#fff" strokeWidth="1.6"/>
                <circle cx="12" cy="12" r="2.7" fill="none" stroke="#fff" strokeWidth="1.6"/>
                <circle cx="17" cy="7" r="1" fill="#fff"/>
              </svg>
            </div>
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>Instagram</div>
              <div style={{ fontSize: '11px', color: '#6B7280', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>Follow us</div>
            </div>
          </a>
        )}
        {restaurant?.mapLocation && (
          <a href={restaurant.mapLocation} target="_blank" rel="noopener noreferrer" className="pc-social-card" style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#FFFFFF', borderRadius: '20px', padding: '16px', textDecoration: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.03)', overflow: 'hidden' }}>
            <div className="pc-social-icon" style={{ width: '44px', height: '44px', borderRadius: '14px', background: '#FEF2F2', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#EF4444" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                <circle cx="12" cy="10" r="3"></circle>
              </svg>
            </div>
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>Location</div>
              <div style={{ fontSize: '11px', color: '#6B7280', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>Get directions</div>
            </div>
          </a>
        )}
        {restaurant?.facebookUrl && (
          <a href={restaurant.facebookUrl} target="_blank" rel="noopener noreferrer" className="pc-social-card" style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#FFFFFF', borderRadius: '20px', padding: '16px', textDecoration: 'none', boxShadow: '0 4px 16px rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.03)', overflow: 'hidden' }}>
            <div className="pc-social-icon" style={{ width: '44px', height: '44px', borderRadius: '14px', background: '#EFF6FF', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="#1877F2" xmlns="http://www.w3.org/2000/svg">
                <path d="M22 12c0-5.52-4.48-10-10-10S2 6.48 2 12c0 4.84 3.44 8.87 8 9.8V15H8v-3h2V9.5C10 7.57 11.57 6 13.5 6H16v3h-2c-.55 0-1 .45-1 1v2h3v3h-3v6.95c5.05-.5 9-4.81 9-9.95z"/>
              </svg>
            </div>
            <div style={{ minWidth: 0, overflow: 'hidden' }}>
              <div style={{ fontSize: '13px', fontWeight: 700, color: '#111827' }}>Facebook</div>
              <div style={{ fontSize: '11px', color: '#6B7280', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>Like us</div>
            </div>
          </a>
        )}
      </div>

      {/* Visit Website Card */}
      {restaurant?.websiteUrl && (
        <a
          href={restaurant.websiteUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="pc-website-card pc-fade-up"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '14px',
            background: '#FFFFFF',
            borderRadius: '20px',
            padding: '16px 18px',
            textDecoration: 'none',
            boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
            border: '1px solid rgba(0,0,0,0.03)',
            overflow: 'hidden'
          }}
        >
          <div className="pc-website-icon" style={{ width: '44px', height: '44px', borderRadius: '14px', background: '#FFFBEB', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={COLORS.accent} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="2" y1="12" x2="22" y2="12"></line>
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
            </svg>
          </div>
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            <div style={{ fontSize: '15px', fontWeight: 700, color: '#111827' }}>Visit Our Website</div>
            <div style={{ fontSize: '12px', color: '#6B7280', whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>{restaurant.websiteUrl}</div>
          </div>
          <svg className="pc-arrow" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14"></path>
            <path d="M12 5l7 7-7 7"></path>
          </svg>
        </a>
      )}
    </div>
  );

const renderMenu = () => (
    <div style={{ paddingBottom: cartCount > 0 ? '160px' : '90px' }}>
      {/* Sticky header */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: 'rgba(248, 247, 244, 0.96)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        padding: '12px 16px',
        borderBottom: `1px solid ${COLORS.border}`
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <button
                type="button"
                onClick={() => setCurrentView('home')}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  border: 'none',
                  background: COLORS.card,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={COLORS.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 12H5"></path>
                  <path d="M12 19l-7-7 7-7"></path>
                </svg>
              </button>
              <div>
                <h1 style={{ fontSize: '18px', fontWeight: 800, color: COLORS.text, margin: 0 }}>{restaurant?.restaurantName}</h1>
                <p style={{ fontSize: '12px', color: COLORS.textSecondary, margin: '2px 0 0 0' }}>{cuisineType}</p>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setCurrentView('cart')}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  border: 'none',
                  background: COLORS.card,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  position: 'relative'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="21" r="1"></circle>
                  <circle cx="20" cy="21" r="1"></circle>
                  <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                </svg>
                {cartCount > 0 && (
                  <span style={{
                    position: 'absolute',
                    top: '-4px',
                    right: '-4px',
                    background: COLORS.danger,
                    color: '#fff',
                    fontSize: '9px',
                    fontWeight: 700,
                    width: '16px',
                    height: '16px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>

          {/* Search + Filter */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', marginBottom: '12px' }}>
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              background: COLORS.card,
              borderRadius: '999px',
              padding: '10px 14px',
              border: `1px solid ${COLORS.border}`,
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              maxWidth: '600px'
            }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.textSecondary} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
              <input
                type="text"
                placeholder="Search for dishes..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  border: 'none',
                  outline: 'none',
                  background: 'transparent',
                  fontSize: '14px',
                  color: COLORS.text,
                  width: '100%'
                }}
              />
            </div>
          </div>

          {/* Order Type Selector */}
          {!isShopMode && typeof selectFulfillmentType === 'function' && (
            <div style={{ marginTop: '12px', display: 'flex', justifyContent: 'center' }}>
              <div style={{
                display: 'flex',
                background: COLORS.card,
                borderRadius: '999px',
                padding: '3px',
                border: `1px solid ${COLORS.border}`,
                width: '100%',
                maxWidth: '480px'
              }}>
                {[
                  { key: 'DELIVERY', label: 'Delivery' },
                  { key: 'TAKEAWAY', label: 'Takeaway' },
                  { key: 'DRIVETHROUGH', label: 'Drive-through' }
                ].map((type) => (
                  <button
                    key={type.key}
                    type="button"
                    onClick={() => selectFulfillmentType(type.key)}
                    style={{
                      flex: 1,
                      padding: '8px 6px',
                      borderRadius: '999px',
                      border: 'none',
                      background: fulfillmentType === type.key ? COLORS.accent : 'transparent',
                      color: fulfillmentType === type.key ? '#fff' : COLORS.textSecondary,
                      fontSize: '12px',
                      fontWeight: fulfillmentType === type.key ? 700 : 600,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    {type.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Categories */}
      <div style={{ padding: '12px 16px', maxWidth: '1200px', margin: '0 auto' }}>
        {filteredSections.map((section) => (
          <div key={section.name} id={`pc-category-${section.name}`} style={{ marginBottom: '28px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'flex-end',
              justifyContent: 'space-between',
              marginBottom: '2px',
              padding: '0 4px'
            }}>
              <div>
                <h2 style={{ fontSize: '18px', fontWeight: 800, color: COLORS.text, margin: '0 0 2px 0' }}>
                  {section.name}
                </h2>
                <p style={{ fontSize: '12px', color: COLORS.accent, fontWeight: 600, margin: 0 }}>
                  {section.items.length} Items
                </p>
              </div>
              <button
                type="button"
                onClick={() => setViewAllCategory(section.name)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'transparent',
                  border: 'none',
                  color: COLORS.accent,
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                View All
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="M12 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>

            <ProductCarousel
              section={section}
              cart={cart}
              onAdd={onAdd}
              onUpdateQuantity={updateQuantity}
              onOpenDescription={onOpenDescription}
              restaurantOpen={restaurantOpen}
              getProductDisplayPriceMeta={getProductDisplayPriceMeta}
              getProductQuantity={getProductQuantity}
              LazyImage={LazyImage}
              COLORS={COLORS}
            />
          </div>
        ))}

        {filteredSections.length === 0 && (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: COLORS.textSecondary }}>
            <div style={{ fontSize: '40px', marginBottom: '12px' }}></div>
            <h3 style={{ fontSize: '18px', fontWeight: 700, color: COLORS.text, margin: 0 }}>No items found</h3>
            <p style={{ fontSize: '14px', margin: '8px 0 0 0' }}>Try a different search or filter.</p>
          </div>
        )}
      </div>

      {/* View All Modal */}
      {viewAllCategory && (
        <ViewAllModal
          section={filteredSections.find((s) => s.name === viewAllCategory)}
          cart={cart}
          onAdd={onAdd}
          onUpdateQuantity={updateQuantity}
          onClose={() => setViewAllCategory(null)}
          restaurantOpen={restaurantOpen}
          getProductDisplayPriceMeta={getProductDisplayPriceMeta}
          getProductQuantity={getProductQuantity}
          openCartItemAddonModal={openCartItemAddonModal}
          setEditingSpecialRequest={setEditingSpecialRequest}
          LazyImage={LazyImage}
          COLORS={COLORS}
        />
      )}

      {/* Category Navigation Modal */}
      {showCategoryMenuModal && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 10001,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'flex-end'
          }}
          onClick={() => setShowCategoryMenuModal(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: COLORS.bg,
              width: '100%',
              maxHeight: '70vh',
              borderRadius: '28px 28px 0 0',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <div style={{
              padding: '16px 16px 10px',
              background: COLORS.card,
              borderBottom: `1px solid ${COLORS.border}`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <h2 style={{ fontSize: '16px', fontWeight: 800, color: COLORS.text, margin: 0 }}>
                {isShopMode ? 'Products' : 'Menu Categories'}
              </h2>
              <button
                type="button"
                onClick={() => setShowCategoryMenuModal(false)}
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  border: 'none',
                  background: COLORS.bg,
                  color: COLORS.text,
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ overflowY: 'auto', padding: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                {(groupedMenuSections || []).map((section) => (
                  <button
                    key={section.name}
                    type="button"
                    onClick={() => scrollToCategory(section.name)}
                    style={{
                      padding: '12px',
                      borderRadius: '14px',
                      border: `1px solid ${COLORS.border}`,
                      background: COLORS.card,
                      textAlign: 'left',
                      cursor: 'pointer'
                    }}
                  >
                    <div style={{ fontSize: '14px', fontWeight: 700, color: COLORS.text, marginBottom: '4px' }}>{section.name}</div>
                    <div style={{ fontSize: '12px', color: COLORS.textSecondary }}>{section.items.length} items</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

      const renderBottomNav = () => (
    <div className="pc-bottom-nav" style={{
      position: 'fixed',
      bottom: 0,
      left: 0,
      right: 0,
      background: '#FFFFFF',
      borderTop: '1px solid rgba(0,0,0,0.06)',
      zIndex: 9999,
      display: 'flex',
      height: '56px',
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }}>
      {['home', 'menu', 'cart'].map((view) => {
        const isActive = currentView === view;
        const label = view === 'menu' ? (isShopMode ? 'Products' : 'Menu') : view.charAt(0).toUpperCase() + view.slice(1);
        return (
          <button
            key={view}
            type="button"
            onClick={() => setCurrentView(view)}
            className="pc-nav-item"
            style={{
              flex: 1,
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              border: 'none',
              background: 'transparent',
              color: isActive ? COLORS.accent : '#9CA3AF',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: isActive ? 700 : 600,
              padding: '6px 0'
            }}
          >
            {isActive && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: '30%',
                right: '30%',
                height: '3px',
                background: COLORS.accent,
                borderRadius: '0 0 4px 4px'
              }} />
            )}
            <div style={{ width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {view === 'home' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill={isActive ? COLORS.accent : 'none'} stroke={isActive ? 'none' : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                  <polyline points="9 22 9 12 15 12 15 22"/>
                </svg>
              )}
              {view === 'menu' && (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={isActive ? COLORS.accent : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 2v7c0 1.66 1.34 3 3 3h0c1.66 0 3-1.34 3-3V2" />
                  <path d="M6 2v8" />
                  <path d="M6 12v10" />
                  <path d="M18 2a4 4 0 0 0-4 4v6h4z" />
                  <path d="M16 12v10" />
                </svg>
              )}
              {view === 'cart' && (
                <div style={{ position: 'relative' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={isActive ? COLORS.accent : '#9CA3AF'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="9" cy="21" r="1"></circle>
                    <circle cx="20" cy="21" r="1"></circle>
                    <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
                  </svg>
                  {cartCount > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-5px',
                      right: '-7px',
                      background: '#EF4444',
                      color: '#fff',
                      fontSize: '9px',
                      fontWeight: 700,
                      width: '16px',
                      height: '16px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {cartCount}
                    </span>
                  )}
                </div>
              )}
            </div>
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );

const renderStickyCartBar = () => {
    if (cartCount === 0 || currentView !== 'menu') return null;

    const bottomOffset = isMobile ? 'calc(80px + env(safe-area-inset-bottom, 0px))' : '92px';

    return (
      <div style={{
        position: 'fixed',
        left: isMobile ? '10px' : '16px',
        right: isMobile ? '10px' : '16px',
        bottom: bottomOffset,
        zIndex: 900,
        background: COLORS.card,
        borderRadius: '16px',
        padding: isMobile ? '8px 10px' : '10px 14px',
        boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
        border: `1.5px solid ${COLORS.accent}25`,
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        boxSizing: 'border-box'
      }}>
        {/* Delivery Charge slab reduction / dynamic fee notice */}
        {stickyFooterDeliveryInsight && (
          <div style={{
            padding: '6px 8px',
            borderRadius: '10px',
            background: currentTheme.isDark
              ? 'rgba(255, 255, 255, 0.05)'
              : '#F9FAFB',
            border: `1px solid ${COLORS.border}`,
            display: 'flex',
            alignItems: 'center'
          }}>
            <span style={{
              fontSize: '10px',
              fontWeight: '700',
              color: COLORS.text,
              lineHeight: 1.3
            }}>
              {stickyFooterDeliveryInsight.message}
            </span>
          </div>
        )}

        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          width: '100%'
        }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{
              fontSize: '11px',
              fontWeight: '700',
              color: COLORS.textSecondary,
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Next step
            </div>
            <div style={{
              fontSize: '14px',
              fontWeight: '800',
              color: COLORS.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              marginTop: '2px'
            }}>
              {cartCount} item(s) • ₹{Number(cartTotal).toFixed(2)}
            </div>
          </div>
          <button
            type="button"
            onClick={() => setCurrentView('cart')}
            style={{
              border: 'none',
              borderRadius: '10px',
              padding: isMobile ? '8px 12px' : '10px 18px',
              cursor: 'pointer',
              fontWeight: '800',
              fontSize: isMobile ? '12px' : '13px',
              color: '#ffffff',
              background: `linear-gradient(135deg, ${COLORS.accent}, ${currentTheme.secondaryColor || COLORS.accent})`,
              boxShadow: `0 4px 14px ${COLORS.accent}45`,
              whiteSpace: 'nowrap',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            View Cart →
          </button>
        </div>
      </div>
    );
  };

  const renderCart = () => {
    const pricing = typeof getCartPricing === 'function' ? getCartPricing() : {
      subtotal: 0,
      deliveryCharge: 0,
      taxAmount: 0,
      taxPercentage: 0,
      taxEnabled: false,
      totalBeforeDiscount: 0,
      discountAmount: 0,
      total: 0,
      minimumOrderAmount: 0,
      belowMinimum: false,
      appliedCouponCode: null
    };

    const subtotal = typeof getCartSubtotal === 'function' ? getCartSubtotal() : pricing.subtotal;
    const belowMin = fulfillmentType === 'DELIVERY' && pricing.belowMinimum;

    const openSpecialRequestEditor = (item) => {
      if (typeof setEditingSpecialRequest === 'function') {
        setEditingSpecialRequest({
          open: true,
          cartItemId: item.cartItemId,
          text: item.specialInstructions || ''
        });
      }
    };

    return (
      <div style={{ padding: '16px 16px 140px', maxWidth: '720px', margin: '0 auto' }}>
        {/* Cart Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <button
              type="button"
              onClick={() => setCurrentView('menu')}
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '50%',
                border: 'none',
                background: COLORS.card,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={COLORS.text} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5"></path>
                <path d="M12 19l-7-7 7-7"></path>
              </svg>
            </button>
            <div>
              <h1 style={{ fontSize: '22px', fontWeight: 800, color: COLORS.text, margin: 0 }}>Your Cart</h1>
              <p style={{ fontSize: '13px', color: COLORS.textSecondary, margin: '2px 0 0 0' }}>{cartCount} {cartCount === 1 ? 'item' : 'items'} added</p>
            </div>
          </div>
          {cart.length > 0 && (
            <button
              type="button"
              onClick={() => typeof setShowClearCartConfirm === 'function' && setShowClearCartConfirm(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: '#FEE2E2',
                color: COLORS.danger,
                border: 'none',
                padding: '8px 14px',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: 700,
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6"></polyline>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                <line x1="10" y1="11" x2="10" y2="17"></line>
                <line x1="14" y1="11" x2="14" y2="17"></line>
              </svg>
              Clear Cart
            </button>
          )}
        </div>

        {cart.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ fontSize: '72px', marginBottom: '20px', filter: 'grayscale(0.2)' }}>🛒</div>
            <h3 style={{ fontSize: '20px', fontWeight: 800, color: COLORS.text, margin: '0 0 8px 0' }}>Your cart is empty</h3>
            <p style={{ fontSize: '14px', color: COLORS.textSecondary, marginBottom: '24px', maxWidth: '300px', lineHeight: 1.5 }}>
              Looks like you haven't added anything to your cart yet. Let's find some delicious items!
            </p>
            <button
              type="button"
              onClick={() => setCurrentView('menu')}
              style={{
                background: COLORS.accent,
                color: '#fff',
                padding: '14px 28px',
                borderRadius: '999px',
                border: 'none',
                fontWeight: 700,
                fontSize: '15px',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(212, 166, 74, 0.25)'
              }}
            >
              {isShopMode ? 'Browse Products' : 'Browse Menu'}
            </button>
          </div>
        ) : (
          <>
            {/* Cart Items List */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', marginBottom: '24px' }}>
              {cart.map((item) => {
                const basePrice = item.discountPrice || item.price;
                const addonsPrice = (item.selectedAddons || []).reduce((sum, addon) => sum + Number(addon.addonPrice || 0), 0);
                const itemTotal = (basePrice + addonsPrice) * item.quantity;

                return (
                  <div
                    key={item.cartItemId}
                    onClick={() => {
                      if (Array.isArray(item.selectedAddons) && item.selectedAddons.length > 0 && typeof openCartItemAddonModal === 'function') {
                        openCartItemAddonModal(item);
                      }
                    }}
                    style={{
                      background: COLORS.card,
                      borderRadius: '24px',
                      padding: '16px',
                      display: 'flex',
                      gap: '16px',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.03)',
                      border: `1px solid ${COLORS.border}`,
                      cursor: Array.isArray(item.selectedAddons) && item.selectedAddons.length > 0 ? 'pointer' : 'default',
                      position: 'relative'
                    }}
                  >
                    {item.imageUrl ? (
                      <div style={{ width: '80px', height: '80px', borderRadius: '16px', overflow: 'hidden', flexShrink: 0, background: '#f3f4f6' }}>
                        {LazyImage ? (
                          <LazyImage
                            src={resolveMediaUrl(item.imageUrl, '=s600')}
                            alt={item.productName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            placeholderStyle={{ width: '100%', height: '100%', background: '#f3f4f6' }}
                            skeleton
                          />
                        ) : (
                          <SafeImage
                            src={resolveMediaUrl(item.imageUrl, '=s600')}
                            alt={item.productName}
                            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            loading="lazy"
                          />
                        )}
                      </div>
                    ) : (
                      <div style={{
                        width: '80px',
                        height: '80px',
                        borderRadius: '16px',
                        background: 'rgba(212, 166, 74, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        fontSize: '20px',
                        fontWeight: 700,
                        color: COLORS.accent
                      }}>
                        {getInitials(item.productName)}
                      </div>
                    )}

                    <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 700, color: COLORS.text, margin: '0 0 4px 0', lineHeight: 1.3 }}>
                        {item.productName}
                      </h3>

                      {/* Selected Addons */}
                      {item.selectedAddons && item.selectedAddons.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', marginBottom: '8px' }}>
                          {item.selectedAddons.map((addon, idx) => (
                            <span
                              key={`${addon.addonId || addon.optionId}-${idx}`}
                              style={{ fontSize: '12px', color: COLORS.textSecondary }}
                            >
                              + {addon.addonName} ({formatPrice(addon.addonPrice)})
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Special Instructions */}
                      {item.specialInstructions && (
                        <div style={{
                          background: '#F9FAFB',
                          borderRadius: '8px',
                          padding: '6px 10px',
                          fontSize: '12px',
                          color: COLORS.textSecondary,
                          marginBottom: '8px',
                          display: 'inline-block',
                          alignSelf: 'flex-start',
                          border: `1px solid ${COLORS.border}`
                        }}>
                          Note: "{item.specialInstructions}"
                        </div>
                      )}

                      <div style={{ display: 'flex', alignItems: 'center', justifyBetween: 'space-between', marginTop: 'auto', gap: '12px' }}>
                        <span style={{ fontSize: '16px', fontWeight: 800, color: COLORS.text }}>
                          {formatPrice(itemTotal)}
                        </span>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateQuantity(item.cartItemId, -1);
                            }}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              border: `1.5px solid ${COLORS.accent}`,
                              background: '#fff',
                              color: COLORS.accent,
                              fontSize: '16px',
                              fontWeight: 700,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                          >
                            −
                          </button>
                          <span style={{ fontSize: '14px', fontWeight: 700, color: COLORS.text, minWidth: '18px', textAlign: 'center' }}>
                            {item.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              updateQuantity(item.cartItemId, 1);
                            }}
                            style={{
                              width: '28px',
                              height: '28px',
                              borderRadius: '50%',
                              border: 'none',
                              background: COLORS.accent,
                              color: '#fff',
                              fontSize: '16px',
                              fontWeight: 700,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer'
                            }}
                          >
                            +
                          </button>
                        </div>
                      </div>

                      {/* Add/Edit Note Button */}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          openSpecialRequestEditor(item);
                        }}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: COLORS.accent,
                          fontSize: '12px',
                          fontWeight: 600,
                          cursor: 'pointer',
                          padding: 0,
                          marginTop: '8px',
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                        {item.specialInstructions ? 'Edit Note' : 'Add Special Instructions'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Order Summary Summary Panel */}
            <div style={{
              background: COLORS.card,
              borderRadius: '24px',
              padding: '20px',
              border: `1px solid ${COLORS.border}`,
              boxShadow: '0 4px 16px rgba(0,0,0,0.03)',
              marginBottom: '20px'
            }}>
              <h3 style={{ fontSize: '15px', fontWeight: 800, color: COLORS.text, margin: '0 0 12px 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Order Summary
              </h3>

              {/* Fulfillment type selector (in cart page) */}
              {!isShopMode && typeof selectFulfillmentType === 'function' && (
                <div style={{ marginBottom: '16px' }}>
                  <div style={{ fontSize: '13px', fontWeight: 700, color: COLORS.text, marginBottom: '8px' }}>
                    Fulfillment Type
                  </div>
                  <div style={{ display: 'flex', background: '#F3F4F6', borderRadius: '12px', padding: '4px', border: `1px solid ${COLORS.border}` }}>
                    {[
                      { key: 'DELIVERY', label: 'Delivery' },
                      { key: 'TAKEAWAY', label: 'Takeaway' },
                      { key: 'DRIVETHROUGH', label: 'Drive-through' }
                    ].map((type) => (
                      <button
                        key={type.key}
                        type="button"
                        onClick={() => selectFulfillmentType(type.key)}
                        style={{
                          flex: 1,
                          padding: '8px',
                          borderRadius: '8px',
                          border: 'none',
                          background: fulfillmentType === type.key ? '#fff' : 'transparent',
                          color: fulfillmentType === type.key ? COLORS.text : COLORS.textSecondary,
                          fontSize: '12px',
                          fontWeight: fulfillmentType === type.key ? 700 : 600,
                          cursor: 'pointer',
                          boxShadow: fulfillmentType === type.key ? '0 2px 6px rgba(0,0,0,0.04)' : 'none',
                          transition: 'all 0.2s'
                        }}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Pricing Rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: COLORS.textSecondary }}>
                  <span>Subtotal</span>
                  <span style={{ fontWeight: 700, color: COLORS.text }}>{formatPrice(subtotal)}</span>
                </div>

                {fulfillmentType === 'DELIVERY' && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: COLORS.textSecondary }}>
                    <span>Delivery Charge</span>
                    <span style={{ fontWeight: 700, color: COLORS.text }}>
                      {pricing.deliveryCharge > 0 ? formatPrice(pricing.deliveryCharge) : 'FREE'}
                    </span>
                  </div>
                )}

                {pricing.discountAmount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#16A34A' }}>
                    <span>Discount ({pricing.appliedCouponCode || 'Coupon'})</span>
                    <span style={{ fontWeight: 700 }}>- {formatPrice(pricing.discountAmount)}</span>
                  </div>
                )}

                {pricing.taxAmount > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: COLORS.textSecondary }}>
                    <span>{gstSettings?.taxName || 'GST'} {pricing.taxEnabled ? `(${pricing.taxPercentage}%)` : ''}</span>
                    <span style={{ fontWeight: 700, color: COLORS.text }}>{formatPrice(pricing.taxAmount)}</span>
                  </div>
                )}
              </div>

              {/* Divider */}
              <div style={{ borderTop: `1px dashed ${COLORS.border}`, margin: '12px 0', paddingTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '16px', fontWeight: 800, color: COLORS.text }}>Total Amount</span>
                  <span style={{ fontSize: '22px', fontWeight: 900, color: COLORS.text }}>
                    {formatPrice(pricing.total)}
                  </span>
                </div>
                <p style={{ fontSize: '11px', color: COLORS.textSecondary, margin: '4px 0 0 0', textAlign: 'right' }}>
                  {pricing.taxAmount > 0 ? 'Inclusive of all taxes' : 'Taxes not applicable'}
                </p>
              </div>

              {/* Delivery charge details slab messaging */}
              {fulfillmentType === 'DELIVERY' && deliverySlabInsight && (
                <div style={{
                  marginTop: '12px',
                  padding: '10px 14px',
                  borderRadius: '12px',
                  background: '#F9FAFB',
                  border: `1px solid ${COLORS.border}`,
                  fontSize: '12px',
                  fontWeight: 600,
                  color: COLORS.text,
                  lineHeight: 1.4
                }}>
                  <div>
                    {deliverySlabInsight.message}
                  </div>
                  <div style={{
                    marginTop: '4px',
                    fontSize: '11px',
                    color: COLORS.textSecondary,
                    fontWeight: '600'
                  }}>
                    Based on current delivery slabs
                  </div>
                </div>
              )}
            </div>

            {/* Below Minimum order for delivery warning */}
            {belowMin && (
              <div style={{
                background: '#FFF7ED',
                color: '#C2410C',
                padding: '12px 16px',
                borderRadius: '16px',
                fontSize: '13px',
                fontWeight: 600,
                marginBottom: '20px',
                border: '1px solid #FED7AA',
                lineHeight: 1.4
              }}>
                Minimum order amount for delivery is Rs {pricing.minimumOrderAmount.toFixed(2)}. Add Rs {(pricing.minimumOrderAmount - subtotal).toFixed(2)} more to proceed.
              </div>
            )}

            {/* Actions */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setCurrentView('menu')}
                style={{
                  width: '100%',
                  padding: '14px',
                  borderRadius: '16px',
                  border: `1.5px solid ${COLORS.accent}`,
                  background: '#fff',
                  color: COLORS.accent,
                  fontWeight: 800,
                  fontSize: '15px',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Add More Items
              </button>
              <button
                type="button"
                onClick={() => {
                  if (belowMin) return;
                  openCheckoutModal();
                }}
                disabled={belowMin}
                style={{
                  width: '100%',
                  padding: '16px',
                  borderRadius: '16px',
                  border: 'none',
                  background: belowMin ? '#D1D5DB' : COLORS.accent,
                  color: '#fff',
                  fontWeight: 800,
                  fontSize: '16px',
                  cursor: belowMin ? 'not-allowed' : 'pointer',
                  boxShadow: belowMin ? 'none' : '0 4px 14px rgba(212, 166, 74, 0.3)',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                Proceed to Checkout
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14"></path>
                  <path d="M12 5l7 7-7 7"></path>
                </svg>
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: COLORS.bg,
      fontFamily: "'Inter', 'Poppins', sans-serif",
      color: COLORS.text,
      paddingBottom: 'env(safe-area-inset-bottom, 0px)'
    }}>
      <style>{`
        @keyframes pc-fade-up {
          from { opacity: 0; transform: translateY(16px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes pc-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .pc-fade-up {
          animation: pc-fade-up 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards;
          opacity: 0;
        }
        .pc-fade-up:nth-child(1) { animation-delay: 0.05s; }
        .pc-fade-up:nth-child(2) { animation-delay: 0.12s; }
        .pc-fade-up:nth-child(3) { animation-delay: 0.19s; }
        .pc-fade-up:nth-child(4) { animation-delay: 0.26s; }
        .pc-float { animation: pc-float 4s ease-in-out infinite; }
        .pc-logo { transition: transform 0.2s ease; }
        .pc-logo:active { transform: scale(0.94); }
        .pc-hero-card { transition: transform 0.3s ease, box-shadow 0.3s ease; }
        .pc-hero-card:hover { transform: translateY(-3px); box-shadow: 0 24px 60px rgba(0,0,0,0.09); }
        .pc-hero-card:active { transform: scale(0.98); }
        .pc-social-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .pc-social-card:hover { transform: translateY(-2px); box-shadow: 0 10px 26px rgba(0,0,0,0.07); }
        .pc-social-card:active { transform: scale(0.97); }
        .pc-social-icon { transition: transform 0.2s ease; }
        .pc-social-card:hover .pc-social-icon { transform: scale(1.08); }
        .pc-website-card { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .pc-website-card:hover { transform: translateY(-2px); box-shadow: 0 10px 26px rgba(0,0,0,0.07); }
        .pc-website-card:active { transform: scale(0.98); }
        .pc-website-card:hover .pc-arrow { transform: translateX(4px); }
        .pc-arrow { transition: transform 0.2s ease; }
        .pc-cta { transition: box-shadow 0.2s ease; }
        .pc-cta:hover { box-shadow: 0 10px 28px rgba(212,166,74,0.4); }
        .pc-cta:hover .pc-cta-arrow { transform: translateX(3px); }
        .pc-cta-arrow { transition: transform 0.2s ease; display: inline-block; }
        .pc-bottom-nav { transition: all 0.3s ease; }
        .pc-nav-item { transition: all 0.2s ease; }
        .pc-nav-item:hover { color: #D4A64A; }
        .pc-nav-item:active { transform: scale(0.95); }
        .pc-product-card { transition: transform 0.3s ease, box-shadow 0.3s ease; }
        .pc-product-card:hover { transform: translateY(-4px); box-shadow: 0 14px 36px rgba(0,0,0,0.1); }
        .pc-product-img-wrap { overflow: hidden; border-radius: 18px; }
        .pc-product-img-wrap img, .pc-product-img-wrap .pc-card-img { transition: transform 0.5s ease; }
        .pc-product-card:hover .pc-product-img-wrap img, .pc-product-card:hover .pc-product-img-wrap .pc-card-img { transform: scale(1.05); }
        .pc-fav-btn { transition: transform 0.2s ease, box-shadow 0.2s ease; }
        .pc-fav-btn:hover { transform: scale(1.1); box-shadow: 0 4px 12px rgba(0,0,0,0.12); }
        .pc-product-card { box-shadow: 0 5px 16px rgba(0,0,0,0.04); }
        .pc-carousel-item.is-center .pc-product-card { box-shadow: 0 10px 28px rgba(0,0,0,0.08); }
        .pc-card-title { font-size: 14px; transition: font-size 0.25s ease; }
        .pc-carousel-item.is-center .pc-card-title { font-size: 16px; }
        .pc-add-pill { display: flex; transition: box-shadow 0.2s ease, transform 0.2s ease; }
        .pc-add-pill:hover:not(:disabled) { box-shadow: 0 6px 16px rgba(212,166,74,0.4); transform: translateY(-1px); }
        .pc-add-pill:active:not(:disabled) { transform: scale(0.96); }
        .pc-qty-stepper button { transition: background 0.2s ease; }
        .pc-qty-stepper button:hover { background: rgba(255,255,255,0.35); }
        .pc-card-img { transition: transform 0.4s ease; }
        .pc-arrow-btn {
          display: none;
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #ffffff;
          border: 1px solid rgba(0,0,0,0.08);
          box-shadow: 0 4px 12px rgba(0,0,0,0.08);
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
          transition: all 0.2s ease;
          color: #111827;
        }
        .pc-arrow-btn:hover {
          background: #f9fafb;
          transform: translateY(-50%) scale(1.1);
        }
        .pc-arrow-btn:active {
          transform: translateY(-50%) scale(0.95);
        }
        @media (min-width: 1024px) {
          .pc-arrow-btn {
            display: flex;
          }
        }
      `}</style>



      {currentView === 'home' && renderHome()}
      {currentView === 'menu' && renderMenu()}
      {currentView === 'cart' && renderCart()}

      {!showSplash && currentView === 'menu' && (
        <button
          className={`menu-mobile-fab menu-btn ${cartCount > 0 ? 'shifted-up' : ''}`}
          onClick={() => setShowCategoryMenuModal(true)}
          style={{
            display: 'inline-flex',
            padding: '8px 12px',
            fontSize: '11px',
            gap: '6px',
            borderRadius: '999px',
            boxShadow: '0 4px 10px rgba(0,0,0,0.15)'
          }}
        >
          ☰ {isShopMode ? 'PRODUCTS' : 'MENU'}
        </button>
      )}

      {!showSplash && renderStickyCartBar()}
      {!showSplash && renderBottomNav()}
    </div>
  );
};

const ViewAllModal = ({
  section,
  cart,
  onAdd,
  onUpdateQuantity,
  onClose,
  restaurantOpen,
  getProductDisplayPriceMeta,
  getProductQuantity,
  openCartItemAddonModal,
  setEditingSpecialRequest,
  LazyImage,
  COLORS = COLORS_DEFAULT
}) => {
  if (!section) return null;

  const getCartItem = (productId) => cart.find((item) => item.productId === productId);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10001,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex',
        alignItems: 'flex-end'
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: COLORS.bg,
          width: '100%',
          maxHeight: '85vh',
          borderRadius: '28px 28px 0 0',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        <div style={{
          padding: '20px 20px 12px',
          background: COLORS.card,
          borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: 800, color: COLORS.text, margin: 0 }}>{section.name}</h2>
            <p style={{ fontSize: '13px', color: COLORS.textSecondary, margin: '4px 0 0 0' }}>{section.items.length} items</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '50%',
              border: 'none',
              background: COLORS.bg,
              color: COLORS.text,
              fontSize: '22px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            ×
          </button>
        </div>
        <div style={{ overflowY: 'auto', padding: '16px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          {section.items.map((product) => {
            const qty = getProductQuantity(product.productId);
            const item = getCartItem(product.productId);
            const priceMeta = typeof getProductDisplayPriceMeta === 'function'
              ? getProductDisplayPriceMeta(product)
              : { price: Number(product?.discountPrice || product?.price || 0), estimatedFromModifiers: false };
            const showOriginalPrice = Number(product?.discountPrice) > 0 && Number(product?.price) > Number(product?.discountPrice);

            return (
              <div
                key={product.productId}
                style={{
                  background: COLORS.card,
                  borderRadius: '20px',
                  padding: '14px',
                  display: 'flex',
                  gap: '14px',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.04)',
                  border: `1px solid ${COLORS.border}`
                }}
              >
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '14px',
                  background: '#f3f4f6',
                  overflow: 'hidden',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  {product.imageUrl ? (
                    LazyImage ? (
                      <LazyImage
                        src={resolveMediaUrl(product.imageUrl, '=s600')}
                        alt={product.productName}
                        className="pc-card-img"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        placeholderStyle={{ width: '100%', height: '100%', background: '#f3f4f6' }}
                        skeleton
                      />
                    ) : (
                      <SafeImage src={resolveMediaUrl(product.imageUrl, '=s600')} alt={product.productName} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    )
                  ) : (
                    <span style={{ fontSize: '20px', fontWeight: 700, color: COLORS.accent }}>{getInitials(product.productName)}</span>
                  )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '4px' }}>
                    {product.isVeg !== undefined && product.isVeg !== null && (
                      <div style={{
                        width: '14px',
                        height: '14px',
                        border: `1.5px solid ${product.isVeg ? COLORS.veg : COLORS.nonVeg}`,
                        borderRadius: '2px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        marginTop: '3px'
                      }}>
                        <div style={{ width: '7px', height: '7px', borderRadius: '50%', background: product.isVeg ? COLORS.veg : COLORS.nonVeg }} />
                      </div>
                    )}
                    <h4 style={{ fontSize: '15px', fontWeight: 700, color: COLORS.text, margin: 0, lineHeight: 1.3, flex: 1 }}>{product.productName}</h4>
                  </div>
                  <p style={{
                    fontSize: '12px',
                    color: COLORS.textSecondary,
                    margin: '0 0 8px 0',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}>{product.description}</p>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ fontSize: '15px', fontWeight: 800, color: COLORS.text }}>
                        {priceMeta.estimatedFromModifiers ? 'From ' : ''}{formatPrice(priceMeta.price)}
                      </span>
                      {showOriginalPrice && !priceMeta.estimatedFromModifiers && (
                        <span style={{ fontSize: '12px', color: '#9CA3AF', textDecoration: 'line-through' }}>{formatPrice(product.price)}</span>
                      )}
                    </div>
                    {qty > 0 ? (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        background: COLORS.accent,
                        borderRadius: '999px',
                        padding: '4px'
                      }}>
                        <button
                          type="button"
                          onClick={() => item && onUpdateQuantity(item.cartItemId, -1)}
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            border: 'none',
                            background: 'rgba(255,255,255,0.25)',
                            color: '#fff',
                            fontSize: '14px',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          −
                        </button>
                        <span style={{ fontSize: '13px', fontWeight: 700, color: '#fff', minWidth: '18px', textAlign: 'center' }}>{qty}</span>
                        <button
                          type="button"
                          onClick={() => item && onUpdateQuantity(item.cartItemId, 1)}
                          style={{
                            width: '24px',
                            height: '24px',
                            borderRadius: '50%',
                            border: 'none',
                            background: 'rgba(255,255,255,0.25)',
                            color: '#fff',
                            fontSize: '14px',
                            fontWeight: 700,
                            cursor: 'pointer'
                          }}
                        >
                          +
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        disabled={!restaurantOpen}
                        onClick={() => onAdd(product)}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '999px',
                          border: 'none',
                          background: restaurantOpen ? COLORS.accent : '#D1D5DB',
                          color: '#fff',
                          fontSize: '12px',
                          fontWeight: 700,
                          cursor: restaurantOpen ? 'pointer' : 'not-allowed'
                        }}
                      >
                        Add
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PremiumCarouselTheme;
